import React from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {ANNOUNCEMENT_TYPES} from '../data';
import AppMenu from '../../../../../../components/common/AppMenu';
import {SVG} from '../../../../../../constants/assetLists';
import {AnnouncementFormValues} from '../types';
import AppSwitch from '../../../../../../components/common/AppSwitch';
import moment from 'moment';

type AnnouncementTypeProps = {
  allDepartments?: any;
};

function getUserDepartmentIds(user: any): string[] {
  const dept = user?.departmentId;
  if (dept === 'all') return ['all'];
  if (Array.isArray(dept)) return dept.map((d: any) => d._id);
  if (dept && typeof dept === 'object' && dept._id) return [dept._id];
  return [];
}

function AnnouncementType({allDepartments}: AnnouncementTypeProps) {
  const {
    control,
    setValue,
    watch,
    trigger,
    formState: {errors},
  } = useFormContext<AnnouncementFormValues>();

  const announcementType = watch('announcementType');
  const isQuick = announcementType === 'Quick';
  const isReservation = announcementType === 'Reservation';
  const hideEnabledDeletion = isQuick || isReservation;

  const user = React.useMemo(() => {
    const s = localStorage.getItem('user');
    return s && s !== 'undefined' ? JSON.parse(s) : null;
  }, []);

  const [anchorAnnouncementEl, setAnchorAnnouncementEl] =
    React.useState<HTMLElement | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorAnnouncementEl(event.currentTarget);
  };

  const applyTypeDefaults = React.useCallback(
    (type: string) => {
      const today = moment().format('YYYY-MM-DD');

      if (type === 'Quick') {
        setValue('showInCalendar', false);
        setValue('enabledDeletion', false);

        setValue('schedulingType', 'default');
        setValue('startDate', today);
        setValue('endDate', today);
        const userDepts = getUserDepartmentIds(user);
        if (userDepts.length > 0) setValue('departments', userDepts);

        setValue('assignee', []);
        setValue('enableRespondentCount', false);
        setValue('forms', '');
        setValue('equipments', [{value: ''}]);
        setValue('checklists', []);
        setValue('tasks', []);
      }

      if (type === 'Reservation') {
        setValue('showInCalendar', true);

        setValue('schedulingType', 'by_day_and_time');
        setValue('startDate', today);
        setValue('startTime', '');
        setValue('endTime', '');
        const restaurantDept = allDepartments?.departments?.find(
          (d: any) => d.name === 'מסעדה',
        );
        if (restaurantDept?._id) {
          setValue('departments', [restaurantDept._id]);
        } else {
          const userDepts = getUserDepartmentIds(user);
          if (userDepts.length > 0) setValue('departments', userDepts);
        }

        setValue('assignee', []);
        setValue('enableRespondentCount', false);
        setValue('forms', '');
        setValue('equipments', [{value: ''}]);
        setValue('checklists', []);
        setValue('tasks', []);
        setValue('announcementTitle', '');
      }

      if (type === 'Menu') {
        setValue('showInBrief', true);
        setValue('showInCalendar', false);
        setValue('enabledDeletion', false);

        const restaurantDept = allDepartments?.departments?.find(
          (d: any) => d.name === 'מסעדה',
        );
        if (restaurantDept?._id) {
          setValue('departments', [restaurantDept._id]);
        }
      }
    },
    [setValue, user, allDepartments],
  );

  const handleMenuClose = () => {
    setAnchorAnnouncementEl(null);
  };

  const handleAnnouncementTypeSelect = (value: string) => {
    setValue(
      'announcementType',
      value as AnnouncementFormValues['announcementType'],
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      },
    );

    // Trigger validation for announcement type
    trigger('announcementType');

    // Reset forms field when announcement type changes
    setValue('forms', '');

    // Reset enableRespondentCount when type changes
    // Only enable for Training or Regulation types
    if (value === 'Training' || value === 'Regulation') {
      setValue('enableRespondentCount', true);
    } else {
      setValue('enableRespondentCount', false);
    }

    applyTypeDefaults(value);

    // Trigger validation for fields that might have been reset or unblocked
    trigger([
      'announcementTitle',
      'forms',
      'enableRespondentCount',
    ]);
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="w-full flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <p className="m-0 text-[20px] font-medium text-black">סוג הדיווח</p>

          <Controller
            control={control}
            name="announcementType"
            render={({field: {value}}) => {
              const selectedItem = ANNOUNCEMENT_TYPES.find(
                item => item.value === value,
              );
              return (
                <div className="flex items-center gap-5">
                  <div className="flex items-center gap-1">
                    <div className="w-[20px] h-[20px]">
                      <img
                        src={selectedItem?.icon}
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <span className="text-[20px] font-medium text-black">
                      {selectedItem?.title}
                    </span>
                  </div>

                  <AppMenu
                    anchorEl={anchorAnnouncementEl}
                    open={Boolean(anchorAnnouncementEl)}
                    onClose={handleMenuClose}
                    onOpen={handleMenuOpen}
                    items={ANNOUNCEMENT_TYPES}
                    selectedItem={value}
                    onItemClick={handleAnnouncementTypeSelect}
                    menuIcon={
                      <div className="flex items-center gap-3">
                        <span className="text-[20px] font-medium text-linkBlue">
                          סוג הדיווח
                        </span>
                        <div className="w-[10px] h-[5px]">
                          <img
                            src={SVG.dropdownIcon}
                            alt="icon"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    }
                  />
                </div>
              );
            }}
          />
        </div>

        <div className="flex items-center gap-4">
          <Controller
            control={control}
            name="isUrgent"
            render={({field: {onChange, value}}) => (
              <div className="flex items-center gap-1">
                <AppSwitch onChange={onChange} checked={value} color="error" />
                <p
                  className={`m-0 text-[16px] text-darkerGray ${value ? 'font-semibold' : ''}`}>
                  דחוף
                </p>
              </div>
            )}
          />

          {!isQuick && (
            <Controller
              control={control}
              name="showInCalendar"
              render={({field: {onChange, value}}) => (
                <div className="flex items-center gap-1">
                  <AppSwitch onChange={onChange} checked={value} />
                  <p
                    className={`m-0 text-[16px] text-darkerGray ${value ? 'font-semibold' : ''}`}>
                    הצגה ביומן
                  </p>
                </div>
              )}
            />
          )}

          <Controller
            control={control}
            name="showInBrief"
            render={({field: {onChange, value}}) => (
              <div className="flex items-center gap-1">
                <AppSwitch onChange={onChange} checked={value} />
                <p
                  className={`m-0 text-[16px] text-darkerGray ${value ? 'font-semibold' : ''}`}>
                  הצגה בבריף
                </p>
              </div>
            )}
          />
        </div>

        {!hideEnabledDeletion && (
          <div className="flex items-center gap-4">
            <Controller
              control={control}
              name="enabledDeletion"
              render={({field: {onChange, value}}) => (
                <div className="flex items-center gap-1">
                  <AppSwitch onChange={onChange} checked={value} />
                  <p
                    className={`m-0 text-[16px] text-darkerGray ${value ? 'font-semibold' : ''}`}>
                    אפשר לאחרים למחוק הודעה
                  </p>
                </div>
              )}
            />
          </div>
        )}
      </div>

      {/* Error display for announcementType */}
      {errors.announcementType && (
        <p className="text-red-500 text-sm">
          {errors.announcementType?.message}
        </p>
      )}
    </div>
  );
}

export default AnnouncementType;
